import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useConfirm } from '../../lib/confirm'
import { useStorageUpload, uploadedFileFields } from '../../lib/upload'
import { formatDate } from '../../lib/format'
import type { SafetyDocument } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select,
  Skeleton,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'
import { CloseIcon, DocIcon, PlusIcon } from '../../components/icons'

interface DocsResponse { ok: boolean; items: SafetyDocument[]; categories: [string, string][] }

const FALLBACK_CATEGORIES: [string, string][] = [
  ['policy', 'Safety Policy'], ['risk_assessment', 'Risk Assessment'], ['manual', 'Safety Manual'],
  ['procedure', 'Procedure / SOP'], ['emergency_plan', 'Emergency Plan'],
  ['toolbox', 'Toolbox Talk / Training Material'], ['other', 'Other'],
]

export default function Documents() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [showForm, setShowForm] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const upload = useStorageUpload(`safety_documents/${user?.org_id ?? ''}`, 25 * 1024 * 1024)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops', 'documents'],
    queryFn: () => api.get<DocsResponse>('/ops/documents'),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/ops/documents', body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['ops', 'documents'] }); setShowForm(false) },
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/ops/documents/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ops', 'documents'] }),
  })

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const file = fileRef.current?.files?.[0]
    if (!file) return
    let fileFields: Record<string, string>
    try {
      fileFields = uploadedFileFields(await upload.upload(file), 'file')
    } catch {
      return
    }
    create.mutate({
      title: form.get('title'),
      category: form.get('category'),
      description: form.get('description'),
      version: form.get('version'),
      ...fileFields,
    })
  }

  const items = data?.items ?? []
  const categories = data?.categories?.length ? data.categories : FALLBACK_CATEGORIES
  const categoryLabel = (key?: string) => categories.find(([k]) => k === key)?.[1] ?? 'Other'

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Safety documents</h1>
          <p className="page-head__sub">Policies, risk assessments, manuals and procedures.</p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'Upload document'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => setShowForm((s) => !s)}
          aria-label={showForm ? 'Cancel' : 'Upload document'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Upload a safety document" sub="Accepted: photo or scan, up to 25 MB." />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack">
                {create.isError && (
                  <Alert tone="danger">
                    {create.error instanceof ApiError ? create.error.message : 'Could not upload that document.'}
                  </Alert>
                )}
                {upload.error && <Alert tone="danger">{upload.error}</Alert>}
                {/* PWA: 2 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'Document details', content: (
                      <div className="stack">
                        <Input label="Title" name="title" required />
                        <div className="grid grid--2">
                          <Select label="Category" name="category" defaultValue="other">
                            {categories.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                          </Select>
                          <Input label="Version" name="version" placeholder="e.g. v2" />
                        </div>
                      </div>
                    ) },
                    { title: 'Description', content: (
                      <Input label="Description" name="description" />
                    ) },
                    { title: 'File', content: (
                      <div className="stack">
                        <Input label="File" type="file" ref={fileRef} accept="image/*,application/pdf" required />
                        {upload.progress !== null && (
                          <div className="field__hint">Uploading… {upload.progress}%</div>
                        )}
                      </div>
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={create.isPending || upload.progress !== null}>Upload</Button>
                      <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Documents" />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} w="70%" /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={<DocIcon size={22} />} title="No documents yet" body="Uploaded safety documents will appear here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Title</th><th>Category</th><th>Version</th><th>Uploaded</th><th /></tr>
                </thead>
                <tbody>
                  {items.map((d) => (
                    <tr key={d.id}>
                      <td style={{ fontWeight: 600 }}>
                        {d.file?.url
                          ? <a href={d.file.url} target="_blank" rel="noreferrer">{d.title || 'Document'}</a>
                          : (d.title || 'Document')}
                      </td>
                      <td>{categoryLabel(d.category)}</td>
                      <td>{d.version || '—'}</td>
                      <td>{formatDate(d.createdAt)}</td>
                      <td className="table__num">
                        {d.id && (
                          <Button size="sm" variant="ghost" loading={remove.isPending}
                                  onClick={async () => {
                                    if (await confirm({
                                      title: 'Delete this document?',
                                      message: d.title || 'Document',
                                      destructive: true,
                                    })) remove.mutate(d.id!)
                                  }}>
                            Delete
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
